import type { Session } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { configurationError, supabase } from "./lib/supabase";

type AuthState = { session: Session; signOut: () => Promise<void> };
const AuthContext = createContext<AuthState | null>(null);
export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("Authentication context is unavailable.");
  return value;
};

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(configurationError);
  async function submit(event: FormEvent) {
    event.preventDefault(); setPending(true); setError(undefined);
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) setError(result.error.message);
    setPending(false);
  }
  return <div className="narrow-wrap auth-wrap"><section className="panel confirm-panel"><p className="eyebrow">PRIVATE APPLICATION</p><h1>Sign in to Trading Informant</h1><p>Use the email/password account that was added to this app’s Supabase allowlist.</p>{error && <div className="notice error">{error}</div>}<form className="confirm-form" onSubmit={submit}><label><span>Email</span><input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label><button className="button primary" disabled={pending || !!configurationError}>{pending ? "Signing in…" : "Sign in"}</button></form><p className="form-note">There is no public signup. Other users in the shared Supabase project cannot access this app’s rows.</p></section></div>;
}

function Unauthorized({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return <div className="narrow-wrap auth-wrap"><section className="panel unavailable-state"><h2>This account is not authorized</h2><p>The account is valid in Supabase Auth but is not present in <code>trading_informant.app_users</code>.</p><button className="button" onClick={() => void onSignOut()}>Sign out</button></section></div>;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>();
  const [authorized, setAuthorized] = useState<boolean>();
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (!session) { setAuthorized(undefined); return; }
    void supabase.from("app_users").select("user_id").eq("user_id", session.user.id).maybeSingle().then(({ data, error }) => setAuthorized(!error && !!data));
  }, [session]);
  if (session === undefined || (session && authorized === undefined)) return <div className="narrow-wrap"><div className="notice neutral">Loading secure session…</div></div>;
  if (!session) return <Login />;
  const signOut = async () => { await supabase.auth.signOut(); };
  if (!authorized) return <Unauthorized onSignOut={signOut} />;
  return <AuthContext.Provider value={{ session, signOut }}>{children}</AuthContext.Provider>;
}
